<div class="popup-overlay" *ngIf="isVisible" (click)="closePopup()">
  <div class="popup-content" (click)="$event.stopPropagation()">

    <!-- Header -->
    <div class="popup-header">
      <h2 mat-dialog-title>
        <mat-icon>content_copy</mat-icon>
        Generate Detailed Content
      </h2>
      <button mat-icon-button (click)="closePopup()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Recommendation Summary -->
    <div class="recommendation-summary" *ngIf="recommendation">
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{recommendation.university}}</mat-card-title>
          <mat-card-subtitle>{{recommendation.category}}</mat-card-subtitle>
          <mat-chip [color]="getPriorityColor(recommendation.priority)" selected class="priority-chip">
            {{recommendation.priority}} Priority
          </mat-chip>
        </mat-card-header>
        <mat-card-content>
          <div class="issue-section">
            <h4>Issue Identified</h4>
            <p>{{recommendation.issue}}</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Generate Button -->
    <div class="generate-section" *ngIf="!detailedContent && !isLoading">
      <p>Click the button below to generate detailed, ready-to-implement content for this recommendation.</p>
      <button mat-raised-button color="primary" (click)="generateContent()">
        <mat-icon>auto_awesome</mat-icon>
        Generate Detailed Content
      </button>
    </div>

    <!-- Loading State -->
    <div class="loading-section" *ngIf="isLoading">
      <div class="loading-content">
        <mat-spinner diameter="60" color="primary"></mat-spinner>
        <h3>🎨 Generating Detailed Content...</h3>
        <p>Our AI is creating comprehensive, ready-to-implement content for you.</p>
        <div class="loading-details">
          <p><strong>Category:</strong> {{recommendation?.category}}</p>
          <p><strong>Issue:</strong> {{recommendation?.issue}}</p>
        </div>
        <div class="loading-animation">
          <div class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div class="error-section" *ngIf="error">
      <mat-card class="error-card">
        <mat-card-content>
          <mat-icon color="warn">error</mat-icon>
          <h3>Content Generation Failed</h3>
          <p>{{error}}</p>
          <button mat-raised-button color="primary" (click)="generateContent()">
            <mat-icon>refresh</mat-icon>
            Try Again
          </button>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Debug Section (Remove in production) -->
    <div class="debug-section"
      style="display: none; background: #f0f0f0; padding: 15px; margin: 15px 0; border-radius: 8px; font-family: monospace; font-size: 12px;">
      <h4>🔍 Debug Info:</h4>
      <p><strong>isLoading:</strong> {{isLoading}}</p>
      <p><strong>error:</strong> {{error}}</p>
      <p><strong>detailedContent:</strong> {{detailedContent ? 'EXISTS' : 'NULL'}}</p>
      <p><strong>recommendation:</strong> {{recommendation ? 'EXISTS' : 'NULL'}}</p>
      <div *ngIf="detailedContent">
        <p><strong>Content Overview:</strong> {{detailedContent.content_overview ? 'EXISTS' : 'MISSING'}}</p>
        <p><strong>Content Pieces:</strong> {{detailedContent.content_pieces ? detailedContent.content_pieces.length + '
          pieces' : 'MISSING'}}</p>
      </div>

      <!-- Debug Buttons -->
      <div style="margin-top: 15px;">
        <button mat-button color="primary" (click)="debugVariables()" style="margin-right: 10px;">
          🔍 Debug Variables
        </button>
        <button mat-button color="accent" (click)="forceChangeDetection()" style="margin-right: 10px;">
          🔄 Force Change Detection
        </button>
        <button mat-button color="warn" (click)="testLoadingState()" style="margin-right: 10px;">
          ⚡ Test Loading State
        </button>
        <button mat-button color="primary" (click)="simpleLoadingTest()" style="margin-right: 10px;">
          🧪 Simple Loading Test
        </button>
        <button mat-button color="accent" (click)="testBehaviorSubject()" style="margin-right: 10px;">
          🚀 Test BehaviorSubject
        </button>
        <button mat-button color="warn" (click)="resetPopupData()">
          🧹 Reset Data
        </button>
      </div>
    </div>

    <!-- Generated Content -->
    <div class="generated-content" *ngIf="detailedContent">

      <!-- Content Overview -->
      <mat-card class="content-card">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>overview</mat-icon>
            Content Overview
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p>{{detailedContent.content_overview}}</p>
          <div class="timeline-info" *ngIf="detailedContent.implementation_timeline">
            <strong>Implementation Timeline:</strong> {{detailedContent.implementation_timeline}}
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Content Pieces -->
      <mat-card class="content-card" *ngIf="detailedContent.content_pieces">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>article</mat-icon>
            Content Pieces ({{detailedContent.content_pieces.length}})
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <mat-expansion-panel *ngFor="let piece of detailedContent.content_pieces; let i = index">
            <mat-expansion-panel-header>
              <mat-panel-title>
                {{i + 1}}. {{piece.title}}
              </mat-panel-title>
              <mat-panel-description>
                {{piece.type}}
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="content-piece-details">
              <div class="content-text">
                <h4>Content:</h4>
                <p>{{piece.content}}</p>
              </div>

              <div class="content-meta">
                <div class="meta-item" *ngIf="piece.target_audience">
                  <strong>Target Audience:</strong> {{piece.target_audience}}
                </div>
                <div class="meta-item" *ngIf="piece.platform">
                  <strong>Platform:</strong> {{piece.platform}}
                </div>
                <div class="meta-item" *ngIf="piece.call_to_action">
                  <strong>Call to Action:</strong> {{piece.call_to_action}}
                </div>
                <div class="meta-item" *ngIf="piece.visual_suggestions">
                  <strong>Visual Suggestions:</strong> {{piece.visual_suggestions}}
                </div>
              </div>

              <div class="content-tags" *ngIf="piece.seo_keywords && piece.seo_keywords.length > 0">
                <h4>SEO Keywords:</h4>
                <div class="keyword-chips">
                  <mat-chip *ngFor="let keyword of piece.seo_keywords">{{keyword}}</mat-chip>
                </div>
              </div>

              <div class="content-tags" *ngIf="piece.hashtags && piece.hashtags.length > 0">
                <h4>Hashtags:</h4>
                <div class="hashtag-chips">
                  <mat-chip color="accent" *ngFor="let hashtag of piece.hashtags">{{hashtag}}</mat-chip>
                </div>
              </div>
            </div>
          </mat-expansion-panel>
        </mat-card-content>
      </mat-card>

      <!-- Success Metrics -->
      <mat-card class="content-card" *ngIf="detailedContent.success_metrics">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>trending_up</mat-icon>
            Success Metrics
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <ul>
            <li *ngFor="let metric of detailedContent.success_metrics">{{metric}}</li>
          </ul>
        </mat-card-content>
      </mat-card>

      <!-- Next Steps -->
      <mat-card class="content-card" *ngIf="detailedContent.next_steps">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>next_plan</mat-icon>
            Next Steps
          </mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <ol>
            <li *ngFor="let step of detailedContent.next_steps">{{step}}</li>
          </ol>
        </mat-card-content>
      </mat-card>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button mat-raised-button color="primary" (click)="copyContent(detailedContent)">
          <mat-icon>content_copy</mat-icon>
          Copy All Content
        </button>
        <button mat-raised-button color="accent" (click)="exportContent(detailedContent)">
          <mat-icon>download</mat-icon>
          Export as JSON
        </button>
        <button mat-button (click)="closePopup()">
          <mat-icon>close</mat-icon>
          Close
        </button>
      </div>
    </div>
  </div>
</div>
